import { useEffect, useState } from 'react';
import { Alert, Button, Stack } from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import { useFeedback } from '../common/FeedbackProvider';
import { STORAGE_KEYS } from '../../utils/constants';
import { tokens } from '../../theme/tokens';
import {
  enableWebPush,
  isIosDevice,
  isIosNonSafariBrowser,
  isStandalonePwa,
  isWebPushSupported,
  markPushPromptSeen,
} from '../../push/webPush';

function shouldOffer() {
  try {
    if (sessionStorage.getItem(STORAGE_KEYS.PUSH_PROMPT_AFTER_LOGIN) !== '1') {
      return false;
    }
  } catch {
    return false;
  }
  if (!isWebPushSupported()) return false;
  if (isIosDevice() && isIosNonSafariBrowser()) return false;
  if (isIosDevice() && !isStandalonePwa()) return false;
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission !== 'default') return false;
  return true;
}

function clearPending() {
  try {
    sessionStorage.removeItem(STORAGE_KEYS.PUSH_PROMPT_AFTER_LOGIN);
  } catch {
    /* ignore */
  }
}

/** Fallback in-app ask after sign-in if the OS permission dialog was dismissed. */
export function PushAskBanner() {
  const { user } = useAuth();
  const { showSuccess, showError } = useFeedback();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) {
      setOpen(false);
      return;
    }
    setOpen(shouldOffer());
  }, [user]);

  if (!open) return null;

  async function allow() {
    setBusy(true);
    clearPending();
    markPushPromptSeen();
    try {
      const next = await enableWebPush({ forceRefresh: true });
      setOpen(false);
      if (next === 'denied') {
        showError('Notifications stay off until you allow them in the browser.');
      } else if (next === 'missing-vapid') {
        showError('Phone notifications are not available on this server.');
      } else {
        showSuccess('Notifications on');
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Could not turn on alerts');
      setOpen(false);
    } finally {
      setBusy(false);
    }
  }

  function dismiss() {
    clearPending();
    markPushPromptSeen();
    setOpen(false);
  }

  return (
    <Alert
      severity="info"
      sx={{
        mb: 2,
        bgcolor: tokens.surface,
        color: tokens.ink,
        border: `1px solid ${tokens.border}`,
        '& .MuiAlert-icon': { color: tokens.brandFill },
        '& .MuiAlert-message': { color: tokens.ink },
        '& .MuiAlert-action': { alignItems: 'center', pt: 0 },
      }}
      action={
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            disabled={busy}
            onClick={dismiss}
            sx={{
              color: tokens.inkMuted,
              bgcolor: 'transparent',
              '&:hover': { bgcolor: tokens.primarySoft, color: tokens.ink },
            }}
          >
            Not now
          </Button>
          <Button
            color="secondary"
            size="small"
            variant="contained"
            disabled={busy}
            onClick={() => void allow()}
            sx={{ color: '#FFFFFF', minWidth: 96 }}
          >
            {busy ? 'Turning on…' : 'Turn on'}
          </Button>
        </Stack>
      }
    >
      Turn on phone alerts so you do not miss a broadcast?
    </Alert>
  );
}
